import { useMemo } from 'react';

import { EventMutationTradeProps } from '../event-mutation';
import { EventBuySharesInput } from './event-buy-shares-input';
import { EventExpiration } from './event-expiration';
import EventMutationSetLimitPrice from './event-mutation-set-limit-price';
import EventBuyProfitMultiplierRow from './rows/event-buy-profit-multiplier-row';
import EventBuyProfitRow from './rows/event-buy-profit-row';
import { EventBuyReceive } from './rows/event-buy-receive';

import { WalletBalance } from '@/components/common/balance/wallet-balance';
import { UsdcIcon } from '@/components/common/icons/usdc';
import { ORDER_TYPE, PRICE_DECIMALS } from '@/constants/solana';
import { useFetchOrderbookState } from '@/hooks/solana/query/use-fetch-orderbook-state';
import { useFetchApproxExchangeRate } from '@/hooks/solana/query/use-fetch-quote';
import { getMatchingAmount, mapOrderbook } from '@/lib/utils';
import { useOrderbookStore } from '@/store/orderbook';

export const EventMutationBuyLimit = ({
  outcome,
  setOutcome,
  id,

  setShares,
  shares,
}: EventMutationTradeProps) => {
  const { data: orderbook } = useFetchOrderbookState(Number(id));
  const { price, setPrice } = useOrderbookStore();

  const { data: approxExchangeRate } = useFetchApproxExchangeRate();
  const orderbookData = useMemo(() => {
    return mapOrderbook(orderbook, outcome);
  }, [orderbook, outcome]);

  const {
    matchingAmount,
    totalCostUsdc,
    averageLimitPrice,
    matchingCost,
    nextBestPrice,
  } = useMemo(() => {
    if (!price || !shares) {
      const allOrders = [...orderbookData.asks]
        .filter((ask) => ask.price > 0)
        .sort((a, b) => a.price - b.price);

      return {
        matchingAmount: 0,
        totalCostUsdc: 0,
        averageLimitPrice: 0,
        matchingCost: 0,
        nextBestPrice: allOrders[0]?.price || null,
      };
    }
    return getMatchingAmount(orderbookData, ORDER_TYPE.BUY, +price, +shares);
  }, [orderbookData, price, shares]);

  const totalCost = useMemo(() => {
    if (!shares || !price) return 0;

    return totalCostUsdc / PRICE_DECIMALS;
  }, [shares, price, totalCostUsdc]);

  return (
    <>
      <EventMutationSetLimitPrice price={price} setPrice={setPrice} />
      <div className="border border-[#212121]" />

      <EventBuySharesInput
        shares={shares}
        setShares={setShares}
        matchingAmount={matchingAmount}
      />
      <WalletBalance />
      <EventExpiration />
      <div className="border border-[#212121]" />

      <div className="flex flex-col gap-2">
        {/* <EventAveragePriceRow averageLimitPrice={averageLimitPrice} /> */}
        <EventBuyProfitRow shares={shares} totalCost={totalCost} />
        <EventBuyProfitMultiplierRow shares={shares} totalCost={totalCost} />
      </div>
      <div className="border border-[#212121]" />

      <div>
        <EventBuyReceive
          marketId={id}
          price={price}
          receiveAmountWithoutFee={totalCostUsdc / PRICE_DECIMALS}
          matchingAmount={
            isNaN(matchingCost) ? 0 : matchingCost / PRICE_DECIMALS
          }
          nextBestPrice={nextBestPrice}
          tradeType="buy"
          solAmount={
            (totalCostUsdc / PRICE_DECIMALS) *
            (approxExchangeRate?.solPerUsdc || 0)
          }
          avgPrice={averageLimitPrice}
        />
        <div className="flex flex-row">
          <div className="grow text-[20px] font-medium sm:text-[24px]">
            You Win
          </div>
          <div className="flex flex-row items-center justify-center gap-[10px] text-[20px] font-medium sm:text-[24px]">
            {isNaN(+shares) || isNaN(+price) ? 0 : Number(shares).toFixed(2)}
            <UsdcIcon className="size-[24px] sm:size-[32px]" />
          </div>
        </div>
      </div>
    </>
  );
};
